import type { FunctionComponent } from 'react';
import React from 'react';
import cx from 'classnames';

import { CardFrontTestIds } from '@trello/test-ids';

import { CardFrontName } from './CardFrontName';
import { useMinimalCardFrontQuery } from './MinimalCardFrontQuery.generated';
import { useCardFrontQueryHookOptions } from './useCardFrontQueryHookOptions';

import styles from './MinimalCardFront.less';

// Minimal cards with undefined names should still render a non-breaking
// character, for a baseline estimate of height.
const ZERO_WIDTH_NON_JOINING_CHARACTER = '\u200c';

interface EmbeddedMinimalCardProps {
  cardId: string;
  name?: string;
  url?: string;
  /**
   * Should really only be used by the MinimalCardFront component, for the sake
   * of allowing us to minimize the number of DOM elements in each minimal card.
   * Do not rely on this externally; it isn't intended to be reliable.
   */
  dangerous_className?: string;
}

/**
 * Embedded representation of the MinimalCard component. Designed to be used for
 * internal use within the CardFront component, where containing styles and data
 * are already available.
 */
export const EmbeddedMinimalCard: FunctionComponent<
  EmbeddedMinimalCardProps
> = ({
  cardId,
  name = ZERO_WIDTH_NON_JOINING_CHARACTER,
  url = '',
  dangerous_className,
}) => (
  <div
    className={cx(styles.details, dangerous_className)}
    data-card-id={cardId}
    data-testid={CardFrontTestIds.MinimalCard}
  >
    <CardFrontName name={name} url={url} />
  </div>
);

interface MinimalCardFrontProps {
  cardId: string;
  skip?: boolean;
}

/**
 * The MinimalCardFront component renders the thinnest possible representation
 * of a card front, with only the card name and required styles.
 */
export const MinimalCardFront: FunctionComponent<MinimalCardFrontProps> = ({
  cardId,
  skip = false,
}) => {
  const { data } = useMinimalCardFrontQuery({
    skip,
    variables: { cardId },
    ...useCardFrontQueryHookOptions(),
  });

  return (
    <EmbeddedMinimalCard
      cardId={cardId}
      name={data?.card?.name}
      url={data?.card?.url}
      dangerous_className={styles.cardFront}
    />
  );
};
